import type React from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Model } from '../types.type';
import models from '../data/models.json';
import { ROUTER_LINKS, BACKEND_KEYS } from '../../common/consts/app-keys.const';
import Slideshow from '../components/ui/slider/Slider';
import type { Slide } from '../components/ui/slider/Slider';
import DownloadIcon from '../components/ui/downloadIcon/DownloadIcon';
import { BackIcon } from '../../common/components/icons/back-icon.component';
import CharactersSection from '../../common/components/characters/characters.component';
import useAuth from '../../auth/feature/auth';
import { HttpFactoryService } from '../../common/services';

const httpService = new HttpFactoryService().createAuthHttpService();

const ModelPageContainer: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const model = models.find((m) => String(m.id) === String(id)) as Model | undefined;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!model) {
    return <Navigate to={ROUTER_LINKS.NOT_FOUND} />;
  }

  const handleDownload = async () => {
    if (!isAuthenticated) {
      navigate(
        `${ROUTER_LINKS.SIGNIN}?returnTo=/library/${model.id}&reason=download`
      );
      return;
    }

    try {
      await httpService.post(
        `${BACKEND_KEYS.TRACK_DOWNLOAD}?slug=${model.assetSlug}`,
        {}
      );
    } catch (e) {
      // tracking failure should not block download
      console.error('[download tracking]', e);
    }

    // Trigger download with a versioned filename so customers can tell
    // revisions apart in their downloads folder (e.g. gary_v1.0.0.zip).
    const a = document.createElement('a');
    a.href = model.downloadSrc;
    const ext = model.downloadSrc.split('.').pop() || 'touch';
    a.download = model.version
      ? `${model.id}_v${model.version}.${ext}`
      : model.downloadSrc.split('/').pop() || `${model.id}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const slides: Slide[] = [
    ...model.images.map((img) => ({
      type: 'image' as const,
      url: img.url,
      caption: `${model.name} ${img.id}`
    })),
    ...(model.modelSrc
      ? [{ type: 'viewer' as const, src: model.modelSrc, caption: '3D Preview' }]
      : [])
  ];

  return (
    <div className="md:mt-[22px]">
      <Link
        to={ROUTER_LINKS.MODELS}
        className=" mb-[22px] ml-[24px] md:ml-[78px] flex items-center gap-[8px] "
      >
        <BackIcon />
        <p className="text-base-black font-body text-[18px] font-medium ">
          {t('library.page.back')}
        </p>
      </Link>
      <div className="flex flex-col lg:flex-row px-[24px] md:px-[72px] pb-[24px] md:pb-[32px]">
        <div className="max-[930px]:w-full mb-[32px] lg:mb-0">
          <Slideshow slides={slides} />
        </div>

        <div className="flex flex-col items-start justify-start  lg:ml-[72px] gap-[16px] md:gap-[22px]">
          <div className="flex items-center gap-[12px] flex-wrap">
            <h1 className="text-base-black font-display text-[24px] md:text-[32px] font-medium md:font-semibold">
              {model.name}
            </h1>
            {model.premium && (
              <span className="inline-flex items-center rounded-[999px] px-[10px] py-[3px] text-[11px] font-display font-bold uppercase tracking-[0.06em] text-[#3a2a00] bg-[linear-gradient(135deg,#ffd76b,#f5a623)] shadow-[0_2px_8px_rgba(245,166,35,0.35)]">
                {t('library.premiumBadge')}
              </span>
            )}
            {model.version && (
              <span className="text-content-muted font-body text-[14px] font-regular">
                v{model.version}
              </span>
            )}
          </div>
          <p className="text-base-black font-body text-[16px] md:text-[18px] font-regular whitespace-pre-line">
            {t(`library.models.${model.id}.description`)}
          </p>

          {/* Premium rigs have TouchPose baked into the Maya scene, so the
              rig is non-functional without the plugin installed. On-brand
              amber warning (shared warning tokens, theme-aware) because
              customers were downloading the rig, opening it without
              TouchPose, and hitting a broken file. */}
          {model.premium && (
            <div
              role="alert"
              className="w-full rounded-[12px] bg-warning border border-warning-border border-l-[4px] border-l-warning-accent px-[20px] py-[16px] md:px-[24px] md:py-[20px] mt-[8px] shadow-[0_2px_12px_rgba(217,119,6,0.12)]"
            >
              <p className="text-warning-text font-display text-[18px] md:text-[22px] font-bold uppercase tracking-[0.04em]">
                {t('library.page.requiresTouchposeTitle')}
              </p>
              <p className="text-warning-text font-body text-[15px] md:text-[17px] font-medium mt-[8px] leading-[1.6]">
                {t('library.page.requiresTouchposeBody')}
              </p>
              <Link
                to={ROUTER_LINKS.TOUCHPOSE_DOCS_GETTING_STARTED}
                className="inline-flex items-center mt-[12px] underline text-warning-accent font-body text-[15px] md:text-[16px] font-bold"
              >
                {t('library.page.requiresTouchposeCta')}
              </Link>
            </div>
          )}

          <div className="flex gap-[22px] mt-[8px] md:mt-[20px]">
            <button
              type="button"
              onClick={handleDownload}
              className="flex justify-center items-center gap-[6px] md: gap-[8px] rounded-[32px] border border-blue-600 bg-blue-500  min-w-[198px] h-[42px] md:min-w-[243px] md:h-[52px] hover:shadow-[0_0_0_4px_rgba(93,225,255,0.20)]
 transition-shadow duration-200 text-black font-body text-[16px] md:text-[18px] font-medium"
            >
              {model.premium
                ? t('library.page.downloadRig')
                : t('library.page.download')}
              <DownloadIcon />
            </button>

            {model.rigSrc && (
              <a
                href={model.rigSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-[32px] border border-gray-500 bg-gray-400 dark:bg-gray-700 min-w-[92px] h-[42px] md:min-w-[118px] md:h-[52px] hover:shadow-[0_0_0_4px_rgba(93,225,255,0.20)] transition-shadow duration-200 text-black font-body text-[16px] md:text-[18px] font-medium"
              >
                {t('library.page.getRig')}
              </a>
            )}
          </div>

          {/* .TOUCH explainer — premium rigs have the data baked into the
              Maya scene, so they don't need a separate touch-file import step. */}
          {!model.premium && (
            <div className="flex flex-col xl:flex-row gap-[12px] md:gap-[22px] mt-[32px] md:mt-[44px]">
              <span className="text-base-black font-body text-[18px] font-bold ">
                {t('library.page.touchFormat')}
              </span>
              <p className="text-base-black font-body text-[16px] font-regular whitespace-pre-line">
                {t('library.page.touchFormatDescription')}
              </p>
            </div>
          )}

          {/* Per-model license blurb — rendered when the rig has a
              `library.models.<id>.license` translation defined. */}
          {t(`library.models.${model.id}.license`, { defaultValue: '' }) && (
            <div className="flex flex-col xl:flex-row gap-[12px] md:gap-[22px] mt-[32px] md:mt-[44px]">
              <span className="text-base-black font-body text-[18px] font-bold ">
                {t('library.page.license')}
              </span>
              <p className="text-base-black font-body text-[16px] font-regular whitespace-pre-line">
                {t(`library.models.${model.id}.license`)}
              </p>
            </div>
          )}
        </div>
      </div>
      <CharactersSection />
    </div>
  );
};

export default ModelPageContainer;
